import { useState, type FormEvent } from 'react';
import { faqs } from '@/mocks/faqs';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9sngtd8o1vn02ekuig0';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言！我们会尽快回复~');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });

      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* raw text */ }

      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言！我们会尽快回复~');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message
          || (parsed as { message?: string })?.message
          || (parsed as { meta?: { detail?: string } })?.meta?.detail
          || text;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢你的留言！我们会尽快回复~');
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络异常，请稍后再试');
    }
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
          <div>
            <p className="text-xs md:text-sm font-label text-primary-500 tracking-widest mb-2">
              ✦ CONTACT ✦
            </p>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-8">
              有任何问题或合作意向，欢迎随时联系嫩小兔团队
            </p>

            <div className="flex flex-col gap-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                  <p className="text-sm text-foreground-600">hello@nenxiaotu.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-weibo-line text-accent-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">微博</p>
                  <p className="text-sm text-foreground-600">@嫩小兔官方</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-red-packet-line text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">小红书</p>
                  <p className="text-sm text-foreground-600">@嫩小兔NenXiaoTu</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-tiktok-line text-foreground-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">抖音</p>
                  <p className="text-sm text-foreground-600">@嫩小兔官方</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200/70 overflow-hidden">
              <div className="aspect-[16/9]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3416.2747128552057!2d121.4737!3d31.2304!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35b27058aaaaaaab%3A0x8f3e4a3b4c3ad2b1!2sShanghai!5e0!3m2!1sen!2scn!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="嫩小兔工作室位置"
                ></iframe>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950 mb-6">发送消息</h3>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-4"
              >
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="honeypot-field"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    你的名字
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="请输入你的名字"
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱地址
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请输入你想说的话（最多500字）"
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>

                {formMessage && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto mt-14 md:mt-20">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
            常见问题
          </h2>
          <p className="text-sm text-foreground-600">快速解答你的疑惑</p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-500 text-lg transition-transform duration-300 flex-shrink-0 ${
                    openFaq === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openFaq === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <p className="px-5 md:px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}